import { ConnectionQuality } from 'livekit-client';
import { useTranslation } from 'react-i18next';
import { useLivekitConnectionQuality } from '../../../hooks/useLivekitConnectionQuality';
import styles from './ConnectionIndicator.module.scss';

export function ConnectionIndicator() {
  const { t } = useTranslation();
  const connectionQuality = useLivekitConnectionQuality();
  const networkQuality = qualityConfig[connectionQuality];

  if (!networkQuality) return null;

  const label = t(networkQuality.labelKey);

  return (
    <div
      className={styles.connectionIndicator}
      role="status"
      aria-label={label}
      title={label}
    >
      {[1, 2, 3].map(bar => {
        const barColorClass = getBarColorClass(bar, networkQuality);
        return (
          <div
            key={bar}
            className={`${styles.bar} ${styles[`bar${bar}`]} ${barColorClass}`}
          />
        );
      })}
    </div>
  );
}


function getBarColorClass(
  barNumber: number,
  config: { bars: number; colorClass: string; lightColorClass: string }
) {
  // If offline, all bars are inactive
  if (config.bars === 0) {
    return config.colorClass;
  }

  // Active bars get full color, inactive bars get light color
  if (barNumber <= config.bars) {
    return config.colorClass;
  }
  return config.lightColorClass;
}

const qualityConfig: Record<
  ConnectionQuality,
  { bars: number; colorClass: string; lightColorClass: string; labelKey: string } | null
> = {
  // Full signal: 3 green bars
  [ConnectionQuality.Excellent]: {
    bars: 3,
    colorClass: styles.green,
    lightColorClass: styles.green,
    labelKey: 'common.connectionQuality.excellent',
  },
  // Medium signal: 2 full green + 1 light green (treating Good as full signal too)
  [ConnectionQuality.Good]: {
    bars: 2,
    colorClass: styles.amber,
    lightColorClass: styles.amberLight,
    labelKey: 'common.connectionQuality.good',
  },
  // Low signal: 1 full red + 2 light red
  [ConnectionQuality.Poor]: {
    bars: 1,
    colorClass: styles.red,
    lightColorClass: styles.redLight,
    labelKey: 'common.connectionQuality.poor',
  },
  // Offline: all inactive
  [ConnectionQuality.Lost]: {
    bars: 0,
    colorClass: styles.inactive,
    lightColorClass: styles.inactive,
    labelKey: 'aiInterview.connectionLost',
  },
  [ConnectionQuality.Unknown]: {
    bars: 0,
    colorClass: styles.inactive,
    lightColorClass: styles.inactive,
    labelKey: 'aiInterview.connectionLost',
  },
};

